/**
 * Choosing the move: the planned step first, then waiting, then the other directions, each
 * checked by look-ahead (`safety.ts`). A first pass tries every candidate with the cheap
 * continuations (follow the route, stand still); only if none is good does a second pass try the
 * wider set of escapes. A planned step that was refused is routed around for a while, so the bot
 * looks for another way instead of waiting at the same spot.
 */

import { E } from '../sim';
import type { CaveState, Dir, Input } from '../sim';
import { isCreature } from './danger';
import { DIR_UP, DIRS, isHorizontal, manhattan, moveIn, snapIn, STAY, stepCell } from './inputs';
import { NO_EXIT } from './prospects';
import type { Prospects } from './prospects';
import { HORIZON, LONG_HORIZON, Verifier } from './safety';

const creature = isCreature;
const { STEEL } = E;

/**
 * Scans of look-ahead allowed per decision: about 20 us each on a 40 x 22 cave under Vitest (whose
 * module transform slows the engine), a few us in a bundled build.
 */
const BUDGET = 48;
/** How long a refused step is routed around. */
const AVOID_SCANS = 24;
/**
 * Scans spent holding still, because the planned step was survivable but lost ground, before the
 * bot takes it anyway: things only ever get worse by waiting for good.
 */
const HOLD_LIMIT = 20;
/** A creature this close (in cells, any path) calls for the longer look-ahead. */
const WARY = 10;
/** How long a dropped boulder is followed to see whether it lands on a creature and pays. */
const FUSE_SCANS = 16;

const WAIT: readonly Input[] = [];
const ONCE: readonly (readonly Input[])[] = DIRS.map((d) => [moveIn(d)]);
const RUN: readonly (readonly Input[])[] = DIRS.map((d) => Array.from({ length: LONG_HORIZON - 1 }, () => moveIn(d)));
const WIDE: readonly (readonly Input[])[] = [...ONCE, ...RUN];

/** Can Digby's move in `dir` change anything (not just walk into a wall)? */
function enterable(s: CaveState, me: number, dir: Dir): boolean {
  const c = stepCell(me, dir, s.w, s.h);
  if (c < 0) return false;
  const el = s.cells[c] ?? E.STEEL;
  if (el === E.SPACE || el === E.DIRT || el === E.DIAMOND || el === E.CLOCK || el === E.OUTBOX_OPEN) return true;
  if (el >= E.KEY && el < E.KEY + 3) return true;
  if (el !== E.BOULDER || !isHorizontal(dir)) return false;
  return s.cells[stepCell(c, dir, s.w, s.h)] === E.SPACE;
}

/** Is `c` a dead end: no way on from it but back to `from`? */
function pocket(s: CaveState, c: number, from: number): boolean {
  for (const d of DIRS) {
    const n = stepCell(c, d, s.w, s.h);
    if (n < 0 || n === from) continue;
    const el = s.cells[n] ?? E.STEEL;
    if (el === E.SPACE || el === E.DIRT || el === E.DIAMOND || el === E.OUTBOX_OPEN) return false;
  }
  return true;
}

export class Mover {
  /** Scan until which each cell is routed around. */
  readonly avoid: Int32Array;
  private readonly verifier: Verifier;
  private escape: Input[] = [];
  private escapeAt = -1;
  private held = 0;
  private readonly before: Prospects = { exit: NO_EXIT, diamonds: 0, reachable: 0 };

  constructor(s: CaveState) {
    this.verifier = new Verifier(s);
    this.avoid = new Int32Array(s.w * s.h);
  }

  /** The moves worth trying, best first. */
  private candidates(s: CaveState, me: number, route: readonly Dir[], goal: number): Input[] {
    const out: Input[] = [];
    const first = route[0];
    if (first !== undefined) {
      const next = stepCell(me, first, s.w, s.h);
      const el = s.cells[next] ?? E.STEEL;
      const grab = el === E.DIAMOND || el === E.CLOCK || (el >= E.KEY && el < E.KEY + 3);
      // Snap rather than step in when stepping in would leave Digby in a pocket, or under something
      // that will drop past him once the diamond is gone.
      const above = next >= s.w ? (s.cells[next - s.w] ?? E.STEEL) : E.STEEL;
      const heavy = (above === E.BOULDER || above === E.DIAMOND) && first !== DIR_UP;
      if (grab && route.length === 1 && (heavy || pocket(s, next, me))) out.push(snapIn(first), moveIn(first));
      else {
        out.push(moveIn(first));
        if (grab) out.push(snapIn(first));
      }
    }
    out.push(STAY);
    const others = DIRS.filter((d) => d !== first && enterable(s, me, d));
    if (goal >= 0) {
      const { w, h } = s;
      others.sort((a, b) => manhattan(stepCell(me, a, w, h), goal, w) - manhattan(stepCell(me, b, w, h), goal, w));
    }
    for (const d of others) out.push(moveIn(d));
    return out;
  }

  /** Start a scan's decision: what Digby can get to now, and a fresh look-ahead budget. */
  measure(s: CaveState): Prospects {
    this.verifier.resetBudget(BUDGET);
    return this.verifier.prospects(s, this.before);
  }

  /**
   * The input for this scan, given the route to `goal` (empty to hold still or dodge). Holds to the
   * last proven escape plan when nothing new can be proved within the look-ahead budget.
   */
  choose(s: CaveState, me: number, route: readonly Dir[], goal: number): Input {
    const v = this.verifier;
    const before = this.before;
    const horizon = this.creatureNear(s, me) ? LONG_HORIZON : HORIZON;
    const first = route[0];
    const onward = route.slice(1).map(moveIn);
    const cands = this.candidates(s, me, route, goal);
    let fallback = STAY;
    let longest = -1;
    let safe: Input | null = null;
    let safePlan: Input[] = [];
    for (let pass = 0; pass < 2; pass++) {
      for (const c of cands) {
        const planned = first !== undefined && c.dir === first;
        const conts = pass === 1 ? WIDE : planned && onward.length > 0 ? [onward, WAIT] : [WAIT];
        const verdict = v.check(s, c, conts, before, horizon);
        if (verdict.ok || (planned && verdict.safe && this.held > HOLD_LIMIT)) {
          if (first !== undefined && !planned) this.refuse(s, me, first);
          this.held = planned ? 0 : c === STAY && first !== undefined ? this.held + 1 : this.held;
          return this.commit(s, c, verdict.plan);
        }
        if (verdict.safe && safe === null) {
          safe = c;
          safePlan = verdict.plan;
        } else if (verdict.survived > longest) {
          longest = verdict.survived;
          fallback = c;
        }
        if (v.exhausted) break;
      }
      if (v.exhausted) break;
    }
    if (first !== undefined && (safe === null || safe.dir !== first)) this.refuse(s, me, first);
    // Nothing keeps every prospect: survival first.
    if (safe !== null) return this.commit(s, safe, safePlan);
    // Nothing new could be proved: keep to the last proven plan while it lasts.
    if (this.escapeAt === s.scan && this.escape.length > 0) {
      this.escapeAt++;
      return this.escape.shift() ?? STAY;
    }
    return fallback;
  }

  /**
   * At a fuse: `hit` if pressing `input` now drops a boulder on a creature and brings diamonds in
   * reach; `strain` for a push that will not move this scan (pressing it anyway rolls the dice for
   * the next); else `wait`.
   */
  fuse(s: CaveState, input: Input, push: boolean): 'hit' | 'strain' | 'wait' {
    if (this.verifier.exhausted) return 'wait';
    const { moved, gain, plan } = this.verifier.drop(s, input, FUSE_SCANS, this.before, RUN);
    if (gain > 0) {
      this.commit(s, input, plan);
      return 'hit';
    }
    return push && !moved ? 'strain' : 'wait';
  }

  /** Is a creature within `WARY` steps (as the crow walks) of Digby? */
  private creatureNear(s: CaveState, me: number): boolean {
    const { w, h, cells } = s;
    const mx = me % w;
    const my = (me - mx) / w;
    for (let y = Math.max(0, my - WARY); y <= Math.min(h - 1, my + WARY); y++) {
      const span = WARY - Math.abs(y - my);
      for (let x = Math.max(0, mx - span); x <= Math.min(w - 1, mx + span); x++) {
        if (creature(cells[y * w + x] ?? STEEL)) return true;
      }
    }
    return false;
  }

  /** Make exactly `input` if look-ahead finds it survivable (it becomes the escape plan's start). */
  attempt(s: CaveState, input: Input): boolean {
    const verdict = this.verifier.check(s, input, [WAIT, ...WIDE], this.before);
    if (verdict.safe) this.commit(s, input, verdict.plan);
    return verdict.safe;
  }

  private refuse(s: CaveState, me: number, dir: Dir): void {
    const c = stepCell(me, dir, s.w, s.h);
    if (c >= 0) this.avoid[c] = s.scan + AVOID_SCANS;
  }

  private commit(s: CaveState, input: Input, plan: Input[]): Input {
    this.escape = plan;
    this.escapeAt = s.scan + 1;
    return input;
  }
}
